import { Injectable } from "@angular/core";
import { ExceptionsService } from "@core-services/exceptions.service";
import {
	SwapAmountDefinedFor,
	SwapGQLService,
	SwapMutation,
	SwapOfferAcceptRequest,
	SwapOfferAcceptSuccessResponse,
	SwapOfferCreateRequest,
	SwapRequest,
	SwapSuccessResponse
} from "@gql/offer";
import { SwapConfirmTrade } from "@models/swap-confirm-trade";
import { ITaxWithheld } from "@modules/taxes/domain/entities/taxes.model";
import { Observable, of } from "rxjs";
import { catchError, switchMap } from "rxjs/operators";
import {
	SwapOfferCreateGQLService,
	SwapOfferCreateMutation
} from "../../../api/graphql/offer/generated/swap.offer.create.service.graphql-gen";
import {
	SwapOfferAcceptGQLService,
	SwapOfferAcceptMutation
} from "../../../api/graphql/offer/generated/swap.offer.accept.service.graphql-gen";
import { TradableListService } from "@main-services/currency/tradable_list.service";

@Injectable({
	providedIn: "root"
})
export class TradeSwapService {
	constructor(
		private swapGQLService: SwapGQLService,
		private exceptionsService: ExceptionsService,
		private swapOfferCreateService: SwapOfferCreateGQLService,
		private swapOfferAcceptService: SwapOfferAcceptGQLService,
		private tradableListService: TradableListService
	) {}

	/**
	 * Swap assets confirmation to the API
	 *
	 * @param amount - the amount to swap
	 * @param amountDefinedFor - either from 'TARGET' or 'SOURCE'
	 * @param sourceAssetId - ID of source asset
	 * @param targetAssetId - ID of target asset
	 * @return - Mutation respons of the swap SwapMutation
	 */
	swapAssetGQL(
		amount: string,
		amountDefinedFor: SwapAmountDefinedFor,
		sourceAssetId: string,
		targetAssetId: string,
		sourceWalletId?: string,
		targetWalletId?: string
	): Observable<SwapMutation | null> {
		const request: SwapRequest = {
			amount: { value: amount },
			amountDefinedFor,
			sourceAssetId,
			targetAssetId,
			targetWalletId,
			sourceWalletId
		};

		return this.swapGQLService.mutate({ input: request }).pipe(
			catchError(this.exceptionsService.extractGqlErrorAndRethrow),
			switchMap(({ data }) => {
				// in case progress is returned pass null
				if (data.swap.__typename === "SwapInProgressResponse") {
					return of(null);
				}

				// otherwise pass the data
				return of(data);
			})
		);
	}

	/**
	 * An adapter to convert the response from swapGQL API to the original Rest API response
	 *
	 * @param swapResponse - response from the swapConfirmation
	 * @return SwapConfirmTrade[] - array of SwapConfirmTrade
	 */
	swapResponseAdapter(swapResponse: SwapSuccessResponse): SwapConfirmTrade[] {
		const _swapConfirmTradeBuy = new SwapConfirmTrade();
		const _swapConfirmTradeSell = new SwapConfirmTrade();

		// Since SwapConfirmTrade properties it immutable, need to write a new object

		const source = swapResponse.swap.sourceTrade;
		const target = swapResponse.swap.targetTrade;
		let taxWithheld: Partial<ITaxWithheld> = null;

		const taxWithheldResponse = swapResponse.swap.taxWithheld;

		if (taxWithheldResponse) {
			const { assetId, fiatId, isTaxServiceAvailable, taxAmountAsset, taxAmountFiat, fiatAmountAfterTax } =
				taxWithheldResponse;
			const { fiatWallet, fiatWalletTransaction, tradeId, wallet, amountAsset } = source;
			//for BCI swap we need to get the tax amount from the target trade

			const isSwapBCI = !fiatWalletTransaction?.id || !fiatWallet?.id;

			taxWithheld = {
				assetId: assetId,
				fiatId: fiatId,
				isTaxServiceAvailable: isTaxServiceAvailable,
				taxAmountAsset: taxAmountAsset?.value,
				taxAmountFiat: taxAmountFiat?.value,
				fiatAmountAfterTax: fiatAmountAfterTax?.value,
				transactionId: fiatWalletTransaction?.id || tradeId,
				fiatWalletId: fiatWallet?.id || wallet?.id,
				isSwapBCI,
				fiatAmountBeforeTax: amountAsset?.value
			};
		}

		const swapConfirmTradeBuy = Object.assign(_swapConfirmTradeBuy, {
			cryptocoin_id: source?.assetId,
			amount: source?.amountAsset.value,
			amount_cryptocoin: source?.amountAsset.value,
			wallet_id: source?.wallet.id,
			fiatWalletId: source?.fiatWallet?.id,
			fiatWalletTransactionId: source?.fiatWalletTransaction?.id,
			tradeId: source?.tradeId,
			taxWithheld
		});
		const swapConfirmTradeSell = Object.assign(_swapConfirmTradeSell, {
			cryptocoin_id: target?.assetId,
			amount: target?.amountAsset.value,
			amount_cryptocoin: target?.amountAsset.value,
			wallet_id: target?.wallet.id,
			fiatWalletId: source?.fiatWallet?.id,
			fiatWalletTransactionId: source?.fiatWalletTransaction?.id,
			tradeId: source?.tradeId,
			taxWithheld
		});

		return [swapConfirmTradeBuy, swapConfirmTradeSell];
	}

	convertResponseIntoCompatible(response: SwapOfferAcceptSuccessResponse): SwapSuccessResponse {
		const result: SwapSuccessResponse = {
			swap: {
				swapId: response.swapOffer.swapOfferId,
				isTaxable: response.swapOffer.isTaxable,
				sourceTrade: {
					wallet: response.swapOffer.sourceTrade.assetWallet,
					amountAsset: response.swapOffer.sourceTrade.amountAsset,
					assetId: this.tradableListService.getItemByPid(response.swapOffer.sourceTrade.assetId).getId(),
					executionTime: response.swapOffer.sourceTrade.executionTime,
					feePercentage: {
						value: response.swapOffer.sourceTrade.feePercentage.value
					},
					fiatWallet: response.swapOffer.sourceTrade?.fiatWallet,
					fiatWalletTransaction: response.swapOffer.sourceTrade.fiatWalletTransaction,
					tradeId: response.swapOffer.sourceTrade.tradeId
				},
				targetTrade: {
					wallet: response.swapOffer.targetTrade.assetWallet,
					amountAsset: response.swapOffer.targetTrade.amountAsset,
					assetId: this.tradableListService.getItemByPid(response.swapOffer.targetTrade.assetId).getId(),
					executionTime: response.swapOffer.targetTrade.executionTime,
					feePercentage: {
						value: response.swapOffer.targetTrade.feePercentage.value
					},
					fiatWallet: response.swapOffer.targetTrade?.fiatWallet,
					fiatWalletTransaction: response.swapOffer.targetTrade.fiatWalletTransaction,
					tradeId: response.swapOffer.targetTrade.tradeId
				},
				taxWithheld: response.swapOffer.taxWithheld
			}
		};
		return result;
	}

	swapOffer(
		amount: string,
		amountDefinedFor: SwapAmountDefinedFor,
		sourceAssetId: string,
		targetAssetId: string,
		visionBonus?: boolean
	): Observable<SwapOfferCreateMutation> {
		const request: SwapOfferCreateRequest = {
			amount: { value: amount },
			amountDefinedFor,
			sourceAssetId,
			targetAssetId,
			visionBonus
		};
		return this.swapOfferCreateService.mutate({ input: request }).pipe(
			catchError(this.exceptionsService.extractGqlErrorAndRethrow),
			switchMap(({ data }) => {
				return of(data);
			})
		);
	}

	acceptSwapOffer(swapOfferId: string, sourceWalletId?: string): Observable<SwapOfferAcceptMutation> {
		const request: SwapOfferAcceptRequest = {
			sourceAssetWalletId: sourceWalletId,
			swapOfferId: swapOfferId
		};
		return this.swapOfferAcceptService.mutate({ input: request }).pipe(
			catchError(this.exceptionsService.extractGqlErrorAndRethrow),
			switchMap(({ data }) => {
				return of(data);
			})
		);
	}
}
